import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { typographyClassName, type TypographyVariant } from '../typography/catalog.js';

/** @deprecated Use TypographyVariant — kept for backward compatibility */
type LegacyVariant = 'h1' | 'h2' | 'h3' | 'body' | 'caption';

const LEGACY_MAP: Record<LegacyVariant, TypographyVariant> = {
  h1: 'heading1',
  h2: 'heading2',
  h3: 'heading3',
  body: 'body',
  caption: 'caption',
};

export type TextVariant = TypographyVariant | LegacyVariant;

export interface TextProps extends HTMLAttributes<HTMLElement> {
  variant?: TextVariant;
  as?: 'h1' | 'h2' | 'h3' | 'p' | 'span' | 'label';
  children: ReactNode;
}

function resolveVariant(variant: TextVariant): TypographyVariant {
  if (variant in LEGACY_MAP) {
    return LEGACY_MAP[variant as LegacyVariant];
  }
  return variant as TypographyVariant;
}

function defaultTag(variant: TypographyVariant): ElementType {
  if (variant === 'heading1') return 'h1';
  if (variant === 'heading2') return 'h2';
  if (variant === 'heading3') return 'h3';
  if (variant === 'label') return 'label';
  return 'p';
}

export function Text({ variant = 'body', as, children, className, ...props }: TextProps) {
  const resolved = resolveVariant(variant);
  const Tag: ElementType = as ?? defaultTag(resolved);
  return (
    <Tag
      className={cn(typographyClassName(resolved), className)}
      data-typography={resolved}
      {...props}
    >
      {children}
    </Tag>
  );
}
